/**
 * Error Log Viewer — admin-only Type 1 Standard surface at /admin/errors.
 *
 * Day 63b second real arbi-dev surface, replacing the Day 62a Placeholder.
 * Mirrors FeedbackInbox.tsx structurally (toolbar shape, filter group
 * pattern, slide-in detail panel, AG Grid Infinite Row Model config) and
 * diverges on:
 *
 *   1. PostgREST embed — `from('sentry_events').select('*, sentry_fingerprints(...)')`
 *      gives per-event rows with the joined fingerprint's status +
 *      occurrence_count. Triage operates on fingerprint side; multiple
 *      events of the same fingerprint share status.
 *
 *   2. 3 filter pills — Level (error/warning/info/debug), Environment
 *      (production/arbi-dev/arbi-dev-local/development), Time range
 *      (24h/7d/30d/all). Default filter = Level error+warning,
 *      Environment production+arbi-dev (excludes development + arbi-dev-local
 *      after Day 63b Phase 5 Gap 1 fix), Time last 7d.
 *
 *   3. Banner about unsymbolicated stack traces — pending source-map
 *      verification per BACKLOG line 256. Detail panel surfaces a
 *      "View in Sentry ↗" link as the escape valve for symbolicated
 *      stacks (per consult Q6 + Q7 promotion).
 *
 *   4. Per-fingerprint triage via triage_fingerprint /
 *      bulk_triage_fingerprint RPCs. Detail panel surfaces 3 transitions
 *      (Resolve / Ignore / Reopen). Bulk-action bar surfaces 2 (Mark
 *      Resolved, Mark Ignored) — selection is per-event but RPC is
 *      called per-unique-fingerprint.
 *
 * Auto-regression: receiver Edge Function flips status open if a
 * resolved fingerprint sees a new event after resolved_at. Ignored
 * fingerprints don't auto-regress (Sentry's model). Resolve/Reopen
 * cycle is the standard triage flow; Ignore is "stop showing me this".
 */

import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type RowClickedEvent,
} from 'ag-grid-community';
import { Search, CheckCircle, X, RotateCcw, EyeOff, ExternalLink, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../lib/supabase';
import { reportDatasourceFailure } from '../lib/agGridErrorTracker';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ───────────────────────────────────────────────────────────────

type SentryFingerprintStatus = 'open' | 'resolved' | 'ignored';
type LevelFilter = 'error' | 'warning' | 'info' | 'debug' | null;
type EnvFilter = 'production' | 'arbi-dev' | 'arbi-dev-local' | 'development' | null;
type TimeFilter = '24h' | '7d' | '30d' | 'all';

interface SentryEventRow {
  id: string;
  event_id: string;
  fingerprint: string;
  level: string;
  environment: string;
  release: string | null;
  timestamp: string;
  message: string | null;
  platform: string | null;
  web_url: string | null;
  payload: Record<string, unknown> | null;
  inserted_at: string;
  sentry_fingerprints: {
    status: SentryFingerprintStatus | string;
    occurrence_count: number;
    first_seen: string;
    last_seen: string;
  } | null;
}

interface ToastState {
  type: 'success' | 'error';
  message: string;
}

type GenericSupabaseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Filter constants ──────────────────────────────────────────────────────────

const LEVEL_FILTERS: ReadonlyArray<{ id: LevelFilter; label: string }> = [
  { id: null, label: 'All' },
  { id: 'error', label: 'Error' },
  { id: 'warning', label: 'Warning' },
  { id: 'info', label: 'Info' },
  { id: 'debug', label: 'Debug' },
] as const;

const ENV_FILTERS: ReadonlyArray<{ id: EnvFilter; label: string }> = [
  { id: null, label: 'All' },
  { id: 'production', label: 'Prod' },
  { id: 'arbi-dev', label: 'arbi-dev' },
  { id: 'arbi-dev-local', label: 'arbi-dev-local' },
  { id: 'development', label: 'Dev' },
] as const;

const TIME_FILTERS: ReadonlyArray<{ id: TimeFilter; label: string }> = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'all', label: 'All' },
] as const;

function timeFilterCutoff(t: TimeFilter): string | null {
  if (t === 'all') return null;
  const ms = t === '24h' ? 86_400_000 : t === '7d' ? 604_800_000 : 2_592_000_000;
  return new Date(Date.now() - ms).toISOString();
}

// ── Badges ────────────────────────────────────────────────────────────────────

function LevelBadge({ level }: { level: string }): JSX.Element {
  const cls =
    (
      {
        error: 'bg-danger/15 text-danger',
        warning: 'bg-warning/15 text-warning',
        info: 'bg-info/15 text-info',
        debug: 'bg-surface-3 text-ink-muted',
      } as Record<string, string>
    )[level] || 'bg-surface-3 text-ink-muted';
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {level}
    </span>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls =
    (
      {
        open: 'bg-info/15 text-info',
        resolved: 'bg-success/15 text-success',
        ignored: 'bg-surface-3 text-ink-muted',
      } as Record<string, string>
    )[status] || 'bg-surface-3 text-ink-muted';
  return (
    <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap', cls)}>
      {status}
    </span>
  );
}

// ── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Filter pill component ─────────────────────────────────────────────────────

interface FilterGroupProps<T extends string | null> {
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}

function FilterGroup<T extends string | null>({
  options,
  value,
  onChange,
}: FilterGroupProps<T>): JSX.Element {
  return (
    <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
      {options.map((f) => (
        <button
          key={f.id ?? 'all'}
          onClick={() => onChange(f.id)}
          aria-pressed={value === f.id}
          className={clsx(
            'px-2 py-0.5 text-2xs font-medium rounded transition-colors cursor-pointer whitespace-nowrap',
            value === f.id
              ? 'bg-surface-4 text-ink-bright'
              : 'text-ink-muted hover:text-ink-secondary'
          )}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ toast, onDismiss }: { toast: ToastState | null; onDismiss: () => void }): JSX.Element | null {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  const bg =
    toast.type === 'success'
      ? 'bg-success/15 border-success/30 text-success'
      : 'bg-danger/15 border-danger/30 text-danger';
  return (
    <div
      className={clsx(
        'fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm font-medium shadow-lg',
        bg
      )}
    >
      {toast.message}
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────

interface StackFrame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
}

function extractStackFrames(payload: Record<string, unknown> | null): StackFrame[] {
  if (!payload) return [];
  // Sentry payload shape: data.event.exception.values[0].stacktrace.frames (array)
  const event = payload.event as Record<string, unknown> | undefined;
  if (!event) return [];
  const exception = event.exception as { values?: Array<{ stacktrace?: { frames?: StackFrame[] } }> } | undefined;
  const frames = exception?.values?.[0]?.stacktrace?.frames;
  return Array.isArray(frames) ? frames.slice().reverse() : [];
}

function extractTags(payload: Record<string, unknown> | null): Array<[string, string]> {
  if (!payload) return [];
  const event = payload.event as Record<string, unknown> | undefined;
  if (!event) return [];
  const tags = event.tags;
  if (Array.isArray(tags)) {
    return tags.filter((t): t is [string, string] => Array.isArray(t) && t.length === 2);
  }
  if (tags && typeof tags === 'object') {
    return Object.entries(tags as Record<string, unknown>).map(([k, v]) => [k, String(v)]);
  }
  return [];
}

interface SentryEventDetailPanelProps {
  row: SentryEventRow | null;
  onClose: () => void;
  onTriage: (fingerprint: string, status: SentryFingerprintStatus) => void;
}

function SentryEventDetailPanel({
  row,
  onClose,
  onTriage,
}: SentryEventDetailPanelProps): JSX.Element | null {
  const [showFullStack, setShowFullStack] = useState<boolean>(false);

  useEffect(() => {
    setShowFullStack(false);
  }, [row?.id]);

  if (!row) return null;

  const status = row.sentry_fingerprints?.status ?? 'open';
  const occurrenceCount = row.sentry_fingerprints?.occurrence_count ?? 1;
  const firstSeen = row.sentry_fingerprints?.first_seen ?? row.timestamp;
  const lastSeen = row.sentry_fingerprints?.last_seen ?? row.timestamp;
  const frames = extractStackFrames(row.payload);
  const visibleFrames = showFullStack ? frames : frames.slice(0, 10);
  const tags = extractTags(row.payload);

  const showResolve = status !== 'resolved';
  const showIgnore = status !== 'ignored';
  const showReopen = status !== 'open';

  return (
    <div className="w-96 flex-shrink-0 bg-surface-1 border-l border-border h-full overflow-y-auto pr-2.5">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
        <h3 className="text-sm font-semibold text-ink-bright truncate pr-2">
          {row.message ?? '(no message)'}
        </h3>
        <button
          onClick={onClose}
          className="text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4 space-y-4">
        {/* Top metadata row */}
        <div className="flex items-center gap-2 flex-wrap">
          <LevelBadge level={row.level} />
          <span className="text-2xs text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">
            {row.environment}
          </span>
          {row.release && (
            <span className="text-2xs font-mono text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">
              {row.release.slice(0, 8)}
            </span>
          )}
          {row.platform && (
            <span className="text-2xs text-ink-muted px-1.5 py-0.5 rounded bg-surface-3">
              {row.platform}
            </span>
          )}
          <StatusBadge status={status} />
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Event ID</p>
          <p className="text-2xs font-mono text-ink-secondary break-all">{row.event_id}</p>
        </div>

        <div>
          <p className="text-2xs uppercase tracking-wider text-ink-muted mb-1">Fingerprint</p>
          <p className="text-2xs font-mono text-ink-secondary break-all">{row.fingerprint}</p>
        </div>

        {/* Frequency */}
        <div className="border-t border-border-subtle pt-3 grid grid-cols-3 gap-2">
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted">Occurrences</p>
            <p className="text-sm font-mono text-ink-bright">{occurrenceCount}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted">First seen</p>
            <p className="text-2xs font-mono text-ink-muted">{relativeTime(firstSeen)}</p>
          </div>
          <div>
            <p className="text-2xs uppercase tracking-wider text-ink-muted">Last seen</p>
            <p className="text-2xs font-mono text-ink-muted">{relativeTime(lastSeen)}</p>
          </div>
        </div>

        {/* Stack trace */}
        {frames.length > 0 && (
          <div className="border-t border-border-subtle pt-3">
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2">Stack trace</p>
            <div className="font-mono text-2xs text-ink-secondary space-y-1">
              {visibleFrames.map((f, i) => (
                <div key={i} className="break-all">
                  <span className="text-ink-bright">{f.function ?? '(anonymous)'}</span>
                  {f.filename && (
                    <span className="text-ink-muted">
                      {' '}
                      @ {f.filename}
                      {f.lineno ? `:${f.lineno}` : ''}
                      {f.colno ? `:${f.colno}` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {frames.length > 10 && (
              <button
                onClick={() => setShowFullStack((s) => !s)}
                className="text-2xs text-accent hover:underline mt-2"
              >
                {showFullStack ? 'Collapse stack' : `Expand full stack (${frames.length} frames)`}
              </button>
            )}
          </div>
        )}

        {/* Tag chips */}
        {tags.length > 0 && (
          <div className="border-t border-border-subtle pt-3">
            <p className="text-2xs uppercase tracking-wider text-ink-muted mb-2">Tags</p>
            <div className="flex flex-wrap gap-1">
              {tags.map(([k, v]) => (
                <span
                  key={`${k}:${v}`}
                  className="text-2xs font-mono px-1.5 py-0.5 rounded bg-surface-3 text-ink-secondary"
                >
                  {k}: {v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* View in Sentry */}
        {row.web_url && (
          <div className="border-t border-border-subtle pt-3">
            <a
              href={row.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-accent hover:underline inline-flex items-center gap-1"
            >
              View in Sentry <ExternalLink size={10} />
            </a>
          </div>
        )}

        {/* Triage actions */}
        <div className="border-t border-border-subtle pt-3 space-y-2">
          {showResolve && (
            <button
              onClick={() => onTriage(row.fingerprint, 'resolved')}
              className="arbi-btn-primary text-xs w-full justify-center"
            >
              <CheckCircle size={12} /> Resolve
            </button>
          )}
          {showIgnore && (
            <button
              onClick={() => onTriage(row.fingerprint, 'ignored')}
              className="arbi-btn-ghost text-xs w-full justify-center"
            >
              <EyeOff size={12} /> Ignore
            </button>
          )}
          {showReopen && (
            <button
              onClick={() => onTriage(row.fingerprint, 'open')}
              className="arbi-btn-ghost text-xs w-full justify-center"
            >
              <RotateCcw size={12} /> Reopen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ErrorLogViewer(): JSX.Element {
  useDocumentTitle('Error Log Viewer');

  const gridRef = useRef<AgGridReact<SentryEventRow> | null>(null);
  const gridApiRef = useRef<GridApi<SentryEventRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<LevelFilter>(null);
  const [envFilter, setEnvFilter] = useState<EnvFilter>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('7d');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRows, setSelectedRows] = useState<SentryEventRow[]>([]);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [detailRow, setDetailRow] = useState<SentryEventRow | null>(null);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);
  const hasFilters = Boolean(
    levelFilter || envFilter || timeFilter !== 'all' || searchText.trim()
  );

  const stateRef = useRef<{
    searchText: string;
    levelFilter: LevelFilter;
    envFilter: EnvFilter;
    timeFilter: TimeFilter;
  }>({ searchText, levelFilter, envFilter, timeFilter });
  stateRef.current = { searchText, levelFilter, envFilter, timeFilter };

  // ── Datasource — PostgREST direct with embed ──────────────────
  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, levelFilter: lf, envFilter: ef, timeFilter: tf } = stateRef.current;

    try {
      let query = supabase
        .from('sentry_events')
        .select(
          '*, sentry_fingerprints(status, occurrence_count, first_seen, last_seen)',
          { count: 'exact' }
        )
        .order('timestamp', { ascending: false });

      // V1 default: Level=error+warning when no explicit filter, Environment=
      // production+arbi-dev when no explicit filter. When filter pill is set
      // to "All" (id=null), no level/env filter applied so all rows surface.
      if (lf) {
        query = query.eq('level', lf);
      } else {
        query = query.in('level', ['error', 'warning']);
      }
      if (ef) {
        query = query.eq('environment', ef);
      } else {
        query = query.in('environment', ['production', 'arbi-dev']);
      }

      const cutoff = timeFilterCutoff(tf);
      if (cutoff) query = query.gte('timestamp', cutoff);

      if (st.trim()) {
        const term = st.trim().replace(/[%_]/g, (m) => `\\${m}`);
        query = query.ilike('message', `%${term}%`);
      }

      const { data, error, count } = await query.range(startRow, endRow - 1);
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as unknown as SentryEventRow[];
      const total = count ?? 0;
      setTotalCount(total);
      successCallback(rows, total);
    } catch (e) {
      reportDatasourceFailure(
        'ErrorLogViewer',
        { rpcName: 'sentry_events', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<SentryEventRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, levelFilter, envFilter, timeFilter, getRowsFn]);

  const refreshGrid = useCallback((): void => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [getRowsFn]);

  const onSelectionChanged = useCallback((): void => {
    if (!gridApiRef.current) return;
    setSelectedRows(gridApiRef.current.getSelectedRows());
  }, []);

  // ── Triage ────────────────────────────────────────────────────
  async function triageOne(fingerprint: string, status: SentryFingerprintStatus): Promise<void> {
    const { error } = await callRpc('triage_fingerprint', {
      p_fingerprint: fingerprint,
      p_status: status,
    });
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    showToast({ type: 'success', message: `Fingerprint ${status}` });
    setDetailRow(null);
    refreshGrid();
  }

  async function bulkTriage(status: SentryFingerprintStatus): Promise<void> {
    const fingerprints = Array.from(new Set(selectedRows.map((r) => r.fingerprint)));
    const { error } = await callRpc('bulk_triage_fingerprint', {
      p_fingerprints: fingerprints,
      p_status: status,
    });
    if (error) {
      showToast({ type: 'error', message: error.message });
      return;
    }
    showToast({
      type: 'success',
      message: `${fingerprints.length} fingerprint(s) ${status}`,
    });
    setSelectedRows([]);
    refreshGrid();
  }

  // ── Cell renderers ────────────────────────────────────────────
  const LevelCell = useCallback((p: ICellRendererParams<SentryEventRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <LevelBadge level={p.data.level} />;
  }, []);

  const StatusCell = useCallback((p: ICellRendererParams<SentryEventRow>): JSX.Element | null => {
    if (!p.data) return null;
    const status = p.data.sentry_fingerprints?.status ?? 'open';
    return <StatusBadge status={status} />;
  }, []);

  const TimeCell = useCallback((p: ICellRendererParams<SentryEventRow>): JSX.Element | null => {
    if (!p.data) return null;
    return <span className="font-mono text-xs text-ink-muted">{relativeTime(p.data.timestamp)}</span>;
  }, []);

  const OccurrencesCell = useCallback(
    (p: ICellRendererParams<SentryEventRow>): JSX.Element | null => {
      if (!p.data) return null;
      const count = p.data.sentry_fingerprints?.occurrence_count ?? 1;
      return <span className="font-mono text-xs text-ink-muted">{count}</span>;
    },
    []
  );

  const columnDefs = useMemo<ColDef<SentryEventRow>[]>(
    () =>
      [
        {
          suppressMovable: true,
          resizable: false,
          width: 40,
          maxWidth: 40,
          sortable: false,
          filter: false,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Level',
          field: 'level',
          width: 90,
          cellRenderer: LevelCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Message',
          field: 'message',
          flex: 1,
          minWidth: 280,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-primary)' },
          tooltipField: 'message',
        },
        {
          headerName: 'Env',
          field: 'environment',
          width: 110,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            color: 'var(--ink-secondary)',
            fontSize: '11px',
          },
        },
        {
          headerName: 'Occurrences',
          field: 'sentry_fingerprints.occurrence_count' as keyof SentryEventRow,
          width: 110,
          cellRenderer: OccurrencesCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Status',
          field: 'sentry_fingerprints.status' as keyof SentryEventRow,
          width: 100,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'When',
          field: 'timestamp',
          width: 110,
          cellRenderer: TimeCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
      ] as ColDef<SentryEventRow>[],
    [LevelCell, StatusCell, TimeCell, OccurrencesCell]
  );

  const defaultColDef = useMemo<ColDef<SentryEventRow>>(
    () => ({
      sortable: false,
      resizable: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-2)',
        headerBackgroundColor: 'var(--surface-3)',
        oddRowBackgroundColor: 'var(--surface-2)',
        rowHoverColor: 'var(--surface-3)',
        selectedRowBackgroundColor: 'var(--accent-muted)',
        borderColor: 'var(--border)',
        headerTextColor: 'var(--ink-muted)',
        textColor: 'var(--ink-primary)',
        rowBorder: { color: 'var(--border-subtle)' },
        fontFamily: "'Inter', system-ui, sans-serif",
        fontSize: '12px',
        headerFontSize: '10px',
        headerHeight: 36,
        rowHeight: 42,
        spacing: 4,
      }),
    []
  );

  const onRowClicked = useCallback((event: RowClickedEvent<SentryEventRow>): void => {
    if (!event.data) return;
    const domEvent = event.event as Event | undefined;
    const target = domEvent?.target as HTMLElement | null;
    if (target?.closest('button') || target?.closest('.ag-checkbox-input')) return;
    setDetailRow(event.data);
  }, []);

  return (
    <div className="flex flex-col h-full -m-4">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Error Log Viewer</h1>
            <span className="text-xs text-ink-muted font-mono">
              {totalCount === 1 ? '1 event' : `${totalCount.toLocaleString()} events`}
            </span>
          </div>
        </div>

        {selectedRows.length > 0 ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink-secondary font-medium">
              {selectedRows.length} event(s) selected
            </span>
            <button
              onClick={() => bulkTriage('resolved')}
              className="arbi-btn-ghost text-xs px-2 py-1"
            >
              <CheckCircle size={12} /> Mark Resolved
            </button>
            <button
              onClick={() => bulkTriage('ignored')}
              className="arbi-btn-ghost text-xs px-2 py-1"
            >
              <EyeOff size={12} /> Mark Ignored
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterGroup options={LEVEL_FILTERS} value={levelFilter} onChange={setLevelFilter} />
            <FilterGroup options={ENV_FILTERS} value={envFilter} onChange={setEnvFilter} />
            <FilterGroup options={TIME_FILTERS} value={timeFilter} onChange={setTimeFilter} />
            <div className="relative ml-auto">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none"
              />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search messages..."
                aria-label="Search error messages"
                className="arbi-input pl-8 pr-3 py-1 text-xs w-60"
              />
            </div>
          </div>
        )}
      </div>

      {/* Banner — unsymbolicated stacks (BACKLOG line 256 source-map verification deferred) */}
      <div className="flex-shrink-0 px-4 py-2 bg-info/10 border-b border-info/20 flex items-center gap-2 text-xs text-info">
        <AlertTriangle size={12} />
        <span>
          Stack traces may be unsymbolicated pending source-map verification. Click the View in
          Sentry link in any event detail panel for the symbolicated trace.
        </span>
      </div>

      {/* Grid + optional detail panel */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        <div className="flex-1 p-2" style={{ minHeight: 0 }}>
          <AgGridReact
            ref={gridRef}
            theme={gridTheme}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="infinite"
            cacheBlockSize={200}
            maxBlocksInCache={50}
            rowSelection={{
              mode: 'multiRow',
              enableClickSelection: false,
              checkboxes: true,
              headerCheckbox: false,
            }}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            onSelectionChanged={onSelectionChanged}
            animateRows={false}
            headerHeight={36}
            rowHeight={42}
            suppressCellFocus={true}
            suppressHorizontalScroll={true}
            overlayNoRowsTemplate={
              hasFilters
                ? '<span style="color:var(--ink-muted);font-size:13px">No errors match your current filters</span>'
                : '<span style="color:var(--ink-muted);font-size:13px">No errors logged yet — events emit here when Sentry receives a runtime exception from arbi or arbi-dev</span>'
            }
          />
        </div>

        {detailRow && (
          <SentryEventDetailPanel
            row={detailRow}
            onClose={() => setDetailRow(null)}
            onTriage={triageOne}
          />
        )}
      </div>
    </div>
  );
}
